import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Table2, Trash2 } from 'lucide-react';
import { TABLE_STATUSES, type RestaurantTable } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useTables } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { titleCase } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Toggle,
} from '../components/ui';

interface Draft {
  id?: string;
  number: number;
  name: string;
  capacity: number;
  section: string;
  sortOrder: number;
  isActive: boolean;
  status?: string;
}

const EMPTY: Draft = { number: 1, name: '', capacity: 4, section: '', sortOrder: 0, isActive: true };

export function TablesAdminPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: tables, isLoading, error, refetch } = useTables(true, false);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<RestaurantTable | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['tables'] });
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const body = {
        number: value.number,
        name: value.name.trim(),
        capacity: value.capacity,
        section: value.section.trim() || null,
        sortOrder: value.sortOrder,
        isActive: value.isActive,
        ...(value.id && value.status ? { status: value.status } : {}),
      };
      return value.id ? api.tables.update(value.id, body) : api.tables.create(body);
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success('Table saved.');
    },
    onError: (caught) => failed(caught, 'Could not save that table.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.tables.remove(id),
    onSuccess: (result) => {
      invalidate();
      setRemoving(null);
      toast.success(result.deactivated ? 'Table taken out of service.' : 'Table deleted.');
    },
    onError: (caught) => failed(caught, 'Could not remove that table.'),
  });

  const nextNumber = Math.max(0, ...(tables ?? []).map((table) => table.number)) + 1;

  return (
    <div className="mx-auto max-w-5xl px-5 py-6">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Tables</h1>
          <p className="mt-1 text-sm text-ink-faint">
            The floor plan. Tables with order history are retired rather than deleted.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          onClick={() =>
            setDraft({ ...EMPTY, number: nextNumber, name: `Table ${nextNumber}`, sortOrder: nextNumber })
          }
        >
          New table
        </Button>
      </header>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {tables && tables.length === 0 ? (
        <EmptyState icon={<Table2 size={30} />} title="No tables" message="Add the first table." />
      ) : null}

      {tables && tables.length > 0 ? (
        <Card padded={false} className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-raised/60 text-left text-[11px] uppercase tracking-wider text-ink-faint">
              <tr>
                <th className="px-4 py-3 font-semibold">No.</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Section</th>
                <th className="px-4 py-3 font-semibold">Seats</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Active order</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {tables.map((table) => (
                <tr key={table.id} className="border-t border-line hover:bg-hover">
                  <td className="tnum px-4 py-3 font-medium text-ink">{table.number}</td>
                  <td className="px-4 py-3 text-ink-soft">
                    {table.name}
                    {!table.isActive ? <Badge className="ml-2" tone="neutral">Retired</Badge> : null}
                  </td>
                  <td className="px-4 py-3 text-ink-faint">{table.section ?? '—'}</td>
                  <td className="tnum px-4 py-3 text-ink-soft">{table.capacity}</td>
                  <td className="px-4 py-3">
                    <Badge
                      tone={
                        table.status === 'AVAILABLE'
                          ? 'positive'
                          : table.status === 'WAITING_PAYMENT'
                            ? 'caution'
                            : table.status === 'RESERVED'
                              ? 'info'
                              : 'accent'
                      }
                    >
                      {titleCase(table.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-faint">
                    {table.activeOrder ? `#${table.activeOrder.orderNumber}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        aria-label="Edit"
                        onClick={() =>
                          setDraft({
                            id: table.id,
                            number: table.number,
                            name: table.name,
                            capacity: table.capacity,
                            section: table.section ?? '',
                            sortOrder: table.sortOrder,
                            isActive: table.isActive,
                            status: table.status,
                          })
                        }
                        className="rounded p-1.5 text-ink-faint transition hover:text-accent"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        aria-label="Remove"
                        onClick={() => setRemoving(table)}
                        className="rounded p-1.5 text-ink-faint transition hover:text-danger"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? `Edit Table ${draft.number}` : 'New table'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!draft?.name.trim()}
              onClick={() => draft && save.mutate(draft)}
            >
              Save
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Table number">
                <Input
                  type="number"
                  min={1}
                  value={draft.number}
                  onChange={(event) => setDraft({ ...draft, number: Number(event.target.value) })}
                />
              </Field>
              <Field label="Seats">
                <Input
                  type="number"
                  min={1}
                  value={draft.capacity}
                  onChange={(event) => setDraft({ ...draft, capacity: Number(event.target.value) })}
                />
              </Field>
            </div>
            <Field label="Display name">
              <Input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Terrace 3"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Section">
                <Input
                  value={draft.section}
                  onChange={(event) => setDraft({ ...draft, section: event.target.value })}
                  placeholder="Main Dining"
                />
              </Field>
              <Field label="Sort order">
                <Input
                  type="number"
                  value={draft.sortOrder}
                  onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })}
                />
              </Field>
            </div>
            {draft.id ? (
              <Field label="Status" hint="Reserved is supported; the reservation system itself is Phase 3.">
                <Select
                  value={draft.status}
                  onChange={(event) => setDraft({ ...draft, status: event.target.value })}
                >
                  {TABLE_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {titleCase(value)}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Toggle
              checked={draft.isActive}
              onChange={(isActive) => setDraft({ ...draft, isActive })}
              label="In service"
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title={`Remove Table ${removing?.number ?? ''}?`}
        message="Tables that have carried orders are retired instead of deleted so the history stays valid."
        confirmLabel="Remove"
        loading={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}
